/**
 * AntD ThemeConfig из наших токенов.
 *
 * `brandOverride` — accent-цвета из user-preferences. Если передан,
 * перебивает дефолтный brand из THEME — чтобы AntD-компоненты
 * (Button primary, Menu selected, Switch, Tabs ink-bar) красились в
 * выбранный пользователем цвет.
 */

import type {ThemeConfig} from 'antd';
import {theme as antdTheme} from 'antd';

import {
  fontFamily,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  radius,
  shadow,
  THEME,
  type ThemeMode,
} from './tokens';

export type BrandOverride = {
  brand: string;
  brandHover: string;
  brandActive: string;
  brandSoft: string;
  textOnBrand: string;
  shadowBrand: string;
};

export const buildAntdTheme = (mode: ThemeMode, brandOverride?: BrandOverride): ThemeConfig => {
  const t = THEME[mode];
  const sh = shadow[mode];
  const isDark = mode === 'dark';

  const b: BrandOverride = brandOverride ?? {
    brand: t.color.brand,
    brandHover: t.color.brandHover,
    brandActive: t.color.brandActive,
    brandSoft: t.color.brandSoft,
    textOnBrand: t.color.textOnBrand,
    shadowBrand: sh.brand,
  };

  return {
    // compactAlgorithm ужимает padding/height/font у всех AntD-компонентов
    // на ~10-15% — это даёт ощутимый прирост плотности на десктопе без
    // ручного тюнинга каждого компонента. Композируем с dark/default.
    algorithm: [
      isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      antdTheme.compactAlgorithm,
    ],
    cssVar: {key: 'office-v3'},
    hashed: false,
    token: {
      /* brand */
      colorPrimary: b.brand,
      colorPrimaryHover: b.brandHover,
      colorPrimaryActive: b.brandActive,
      colorInfo: b.brand,
      colorSuccess: t.color.success,
      colorWarning: t.color.warning,
      colorError: t.color.error,

      /* surfaces */
      colorBgBase: t.color.bg,
      colorBgLayout: t.color.bg,
      colorBgContainer: t.color.surface,
      colorBgElevated: t.color.bgElevated,
      // colorBgSpotlight НЕ трогаем — AntD сам подбирает контрастный
      // фон для Tooltip/Popover.

      /* text */
      colorText: t.color.textPrimary,
      colorTextSecondary: t.color.textSecondary,
      colorTextTertiary: t.color.textTertiary,
      colorTextQuaternary: t.color.textDisabled,

      /* border */
      colorBorder: t.color.border,
      colorBorderSecondary: t.color.borderSubtle,

      /* radii */
      borderRadius: radius.md,
      borderRadiusSM: radius.sm,
      borderRadiusLG: radius.lg,
      borderRadiusXS: radius.xs,

      /* typography */
      fontFamily: fontFamily.sans,
      fontFamilyCode: fontFamily.mono,
      fontSize: fontSize.md,
      fontSizeSM: fontSize.sm,
      fontSizeLG: fontSize.lg,
      fontSizeXL: fontSize.xl,
      fontSizeHeading1: fontSize['3xl'],
      fontSizeHeading2: fontSize['2xl'],
      fontSizeHeading3: fontSize.xl,
      fontSizeHeading4: fontSize.lg,
      fontSizeHeading5: fontSize.md,
      fontWeightStrong: fontWeight.semibold,
      lineHeight: lineHeight.normal,
      lineHeightHeading1: lineHeight.tight,

      /* shadows */
      boxShadow: sh.md,
      boxShadowSecondary: sh.sm,
      boxShadowTertiary: sh.sm,

      /* motion */
      motionDurationFast: '120ms',
      motionDurationMid: '180ms',
      motionDurationSlow: '260ms',

      /* sizing — компактнее чем по дефолту, бьётся с compactAlgorithm */
      controlHeight: 32,
      controlHeightSM: 26,
      controlHeightLG: 40,

      wireframe: false,
    },
    components: {
      Layout: {
        headerHeight: layout.headerHeight,
        headerBg: t.color.surface,
        headerPadding: '0 24px',
        bodyBg: t.color.bg,
        siderBg: t.color.siderBg,
        triggerBg: t.color.siderBgElevated,
        triggerColor: t.color.siderText,
      },
      Menu: {
        itemSelectedBg: b.brandSoft,
        itemSelectedColor: b.brand,
        darkItemBg: 'transparent',
        darkItemSelectedBg: b.brand,
        darkItemSelectedColor: b.textOnBrand,
        darkItemHoverBg: t.color.siderBgElevated,
        darkItemHoverColor: b.textOnBrand,
        darkSubMenuItemBg: 'transparent',
        darkPopupBg: t.color.siderBgElevated,
        itemBorderRadius: radius.sm,
        itemHeight: 38,
        iconSize: 16,
      },
      Button: {
        borderRadius: radius.md,
        borderRadiusSM: radius.sm,
        borderRadiusLG: radius.lg,
        fontWeight: fontWeight.medium,
        primaryShadow: b.shadowBrand,
        defaultShadow: 'none',
        dangerShadow: 'none',
        controlHeight: 32,
      },
      Card: {
        borderRadiusLG: radius.lg,
        paddingLG: 16,
        boxShadow: 'none',
        boxShadowTertiary: sh.sm,
      },
      Table: {
        borderRadius: radius.md,
        headerBg: t.color.bgSunken,
        headerColor: t.color.textSecondary,
        headerSplitColor: 'transparent',
        rowHoverBg: t.color.surfaceHover,
        cellPaddingBlock: 10,
        cellFontSize: fontSize.md,
      },
      Input: {
        borderRadius: radius.md,
        controlHeight: 32,
        activeShadow: sh.focus,
        paddingInline: 10,
      },
      Select: {
        borderRadius: radius.md,
        controlHeight: 32,
      },
      Drawer: {colorBgElevated: t.color.surface, borderRadiusLG: 0},
      Modal: {
        borderRadiusLG: radius.lg,
        contentBg: t.color.surface,
        headerBg: t.color.surface,
        footerBg: t.color.surface,
        titleColor: t.color.textPrimary,
        titleFontSize: fontSize.lg,
      },
      Tag: {borderRadiusSM: radius.sm, defaultBg: t.color.bgSunken},
      Tabs: {
        itemActiveColor: b.brand,
        itemSelectedColor: b.brand,
        inkBarColor: b.brand,
      },
      Typography: {
        titleMarginTop: 0,
        titleMarginBottom: '0.5em',
      },
      Switch: {
        colorPrimary: b.brand,
        colorPrimaryHover: b.brandHover,
      },
      Segmented: {
        itemSelectedBg: t.color.surface,
        itemSelectedColor: b.brand,
        trackBg: t.color.bgSunken,
        borderRadius: radius.md,
      },
    },
  };
};
